/**
 * Product Card Horizontal Layout
 *
 * @package
 */

import { RawHTML } from '@wordpress/element';
import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { getImageAltText } from '../helpers/getImageAltText';
import InstallationBadge from './InstallationBadge';
import QuantitySelector from './QuantitySelector';

const ProductCardHorizontal = ( {
	productData,
	isSelected,
	isLocked,
	isLoading,
	onToggle,
	onInfoClick,
	formattedPrice,
	installationHours,
	allowQuantity,
	quantity,
	onQuantityChange,
} ) => {
	const handleKeyDown = e => {
		if ( e.key === 'Enter' || e.key === ' ' ) {
			e.preventDefault();
			if ( ! isLocked ) {
				onToggle();
			}
		}
	};

	const productTitle = decodeHtmlEntities( productData.title?.rendered ) || __( 'Product', 'at-vehicle-configurator' );

	return (
		<div
			className={ `configurator-product-card configurator-product-card-horizontal ${ isSelected ? 'selected' : '' } ${ isLocked ? 'locked' : '' } ${ isLoading ? 'checkbox-loading' : '' } checkbox-mode` }
			role="button"
			tabIndex={ isLocked ? -1 : 0 }
			onClick={ () => ! isLocked && onToggle() }
			onKeyDown={ handleKeyDown }
			aria-label={ `${ isLocked ? __( 'Locked: ', 'at-vehicle-configurator' ) : '' }${ isSelected ? __( 'Selected: ', 'at-vehicle-configurator' ) : __( 'Select ', 'at-vehicle-configurator' ) }${ productTitle }` }
			aria-pressed={ isSelected }
			aria-disabled={ isLocked }
		>
			{ productData.featured_media_url && (
				<div className="configurator-product-card-image-wrapper">
					<img
						src={ productData.featured_media_url }
						alt={ getImageAltText( productData, 'Product' ) }
						loading="lazy"
						decoding="async"
					/>
					<button
						type="button"
						className="configurator-product-card-info-icon"
						onClick={ onInfoClick }
						aria-label={ sprintf(
							/* translators: %s: product name */
							__( 'View information about %s', 'at-vehicle-configurator' ),
							productTitle
						) }
					>
						<span className="screen-reader-text">{ __( 'Information', 'at-vehicle-configurator' ) }</span>
						i
					</button>
				</div>
			) }
			<div className="configurator-product-card-content">
				<h4>{ productTitle }</h4>
				{ productData.excerpt?.rendered && (
					<div className="configurator-product-card-description">
						<RawHTML>{ productData.excerpt.rendered }</RawHTML>
					</div>
				) }
				{ formattedPrice && (
					<div className="configurator-product-price">
						{ formattedPrice }
						<InstallationBadge hours={ installationHours } />
					</div>
				) }
				{ allowQuantity && isSelected && (
					<QuantitySelector
						quantity={ quantity }
						onChange={ units => onQuantityChange( units ) }
						productTitle={ productTitle }
						disabled={ isLocked }
					/>
				) }
			</div>
		</div>
	);
};

ProductCardHorizontal.propTypes = {
	productData: PropTypes.shape( {
		title: PropTypes.shape( {
			rendered: PropTypes.string,
		} ),
		excerpt: PropTypes.shape( {
			rendered: PropTypes.string,
		} ),
		featured_media_url: PropTypes.string,
	} ).isRequired,
	isSelected: PropTypes.bool.isRequired,
	isLocked: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool,
	onToggle: PropTypes.func.isRequired,
	onInfoClick: PropTypes.func.isRequired,
	formattedPrice: PropTypes.string,
	installationHours: PropTypes.number,
	allowQuantity: PropTypes.bool,
	quantity: PropTypes.number,
	onQuantityChange: PropTypes.func,
};

ProductCardHorizontal.defaultProps = {
	isLoading: false,
	formattedPrice: null,
	installationHours: 0,
	allowQuantity: false,
	quantity: 1,
	onQuantityChange: null,
};

export default ProductCardHorizontal;
